import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Users, Search, Printer, Nfc, QrCode, Upload, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Label, Avatar, scoreColor, EmptyState } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

export default function Employees() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [emps, setEmps] = useState([]);
  const [locations, setLocations] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", location_id: "", job_title: "", photo_url: "", show_photo: true, hire_date: "",
    create_account: false, account_email: "", account_password: "" });
  const [bulkLoc, setBulkLoc] = useState("");

  const goBulk = () => {
    const loc = user.role === "store_admin" ? user.location_id : bulkLoc;
    if (!loc) { toast.error("Choose a location to print"); return; }
    navigate(`/app/print/location/${loc}`);
  };

  const load = () => api.get("/employees").then((r) => setEmps(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/locations").then((r) => setLocations(r.data)).catch(() => {}); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const onPhotoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Photo must be under 2 MB."); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, photo_url: reader.result }));
    reader.onerror = () => toast.error("Could not read that image.");
    reader.readAsDataURL(file);
  };

  const create = async () => {
    if (!form.name || (user.role === "corporate_admin" && !form.location_id)) {
      toast.error("Name and location are required"); return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.hire_date) delete payload.hire_date;
      await api.post("/employees", payload);
      toast.success("Employee added with QR identity");
      setOpen(false);
      setForm({ name: "", location_id: "", job_title: "", photo_url: "", show_photo: true, hire_date: "",
        create_account: false, account_email: "", account_password: "" });
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const filtered = emps.filter((e) => e.name.toLowerCase().includes(q.toLowerCase()));
  const admins = filtered.filter((e) => e.is_admin);
  const staff = filtered.filter((e) => !e.is_admin);
  const isCorp = user.role === "corporate_admin";

  const [promoteFor, setPromoteFor] = useState(null);
  const [pForm, setPForm] = useState({ email: "", password: "", location_ids: [] });
  const openPromote = (e) => {
    setPromoteFor(e);
    setPForm({ email: e.admin_email || "", password: "", location_ids: e.admin_location_ids?.length ? e.admin_location_ids : [e.location_id] });
  };
  const toggleLoc = (id) => setPForm((f) => ({ ...f, location_ids: f.location_ids.includes(id) ? f.location_ids.filter((x) => x !== id) : [...f.location_ids, id] }));
  const savePromote = async () => {
    try {
      const body = { email: pForm.email.trim().toLowerCase(), location_ids: pForm.location_ids };
      if (pForm.password) body.password = pForm.password;
      await api.post(`/employees/${promoteFor.id}/promote`, body);
      toast.success("Location admin rights assigned"); setPromoteFor(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const demote = async () => {
    try { await api.post(`/employees/${promoteFor.id}/demote`); toast.success("Admin rights removed"); setPromoteFor(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const Card = (e) => (
    <div key={e.id} data-testid={`employee-row-${e.id}`}
      className="relative flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 text-left transition-transform hover:-translate-y-0.5 hover:border-indigo-300">
      {isCorp && (
        <button onClick={(ev) => { ev.stopPropagation(); openPromote(e); }} data-testid={`promote-${e.id}`}
          className={`absolute right-2 top-2 rounded-full p-1.5 ${e.is_admin ? "bg-indigo-100 text-indigo-700" : "text-slate-300 hover:bg-slate-100 hover:text-indigo-600"}`} title={e.is_admin ? "Manage admin rights" : "Make location admin"}>
          <ShieldCheck className="h-4 w-4" />
        </button>
      )}
      <button onClick={() => navigate(`/app/employees/${e.id}`)} className="flex min-w-0 flex-1 items-center gap-3 text-left">
        <Avatar name={e.name} src={e.photo_url} size={48} />
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-slate-800">{e.name}</p>
          <p className="truncate text-xs text-slate-400">{e.job_title || "—"}</p>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${e.status === "active" ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>
              <span className={`inline-block h-1.5 w-1.5 rounded-full ${e.status === "active" ? "bg-emerald-500" : "bg-slate-300"}`} />
              {e.status}
            </span>
            {e.is_admin && (
              <span className="inline-flex items-center gap-1 rounded-full bg-indigo-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white" data-testid={`admin-badge-${e.id}`}>
                <ShieldCheck className="h-3 w-3" /> Location Admin
              </span>
            )}
            {e.nfc_provisioned ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-600" data-testid={`nfc-badge-${e.id}`}>
                <Nfc className="h-3 w-3" /> NFC
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-600" data-testid={`nfc-badge-${e.id}`}>
                <QrCode className="h-3 w-3" /> QR only
              </span>
            )}
          </div>
        </div>
        <div className="text-right">
          <p className="sv-metric text-xl font-bold" style={{ color: scoreColor(e.score.ssi) }}>{e.score.ssi.toFixed(1)}</p>
          <p className="text-[10px] uppercase text-slate-400">{e.score.verified_interactions} reviews</p>
        </div>
      </button>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Label>Team</Label>
          <h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">Employees</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {user.role === "corporate_admin" && (
            <Select value={bulkLoc} onValueChange={setBulkLoc}>
              <SelectTrigger className="w-40" data-testid="bulk-location"><SelectValue placeholder="Location…" /></SelectTrigger>
              <SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Button variant="outline" onClick={goBulk} data-testid="bulk-print-button"><Printer className="mr-1 h-4 w-4" /> Print lanyards</Button>
          <Button onClick={() => setOpen(true)} data-testid="add-employee-button" className="bg-[#4338CA] hover:bg-[#3730A3]">
            <Plus className="mr-1 h-4 w-4" /> Add employee
          </Button>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search employees…" className="pl-9" data-testid="employee-search" />
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={Users} title="No employees yet" hint="Add your first employee to generate a QR identity."
          action={<Button onClick={() => setOpen(true)} className="bg-[#4338CA]"><Plus className="mr-1 h-4 w-4" />Add employee</Button>} />
      ) : (
        <div className="space-y-6">
          {admins.length > 0 && (
            <div data-testid="admins-section">
              <Label className="mb-2 block">Location Admins</Label>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{admins.map(Card)}</div>
            </div>
          )}
          <div>
            {admins.length > 0 && <Label className="mb-2 block">Employees</Label>}
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{staff.map(Card)}</div>
          </div>
        </div>
      )}

      {/* Promote dialog */}
      <Dialog open={!!promoteFor} onOpenChange={(v) => !v && setPromoteFor(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sv-scroll">
          <DialogHeader><DialogTitle>Location admin rights · {promoteFor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">Give this employee admin access to create employees and manage the selected location(s). They can sign in with email + password or Google (same email).</p>
            <Field label="Admin email"><Input type="email" value={pForm.email} onChange={(e) => setPForm({ ...pForm, email: e.target.value })} data-testid="promote-email" placeholder="name@company.com" /></Field>
            <Field label={promoteFor?.is_admin ? "New temporary password (optional)" : "Temporary password"}>
              <Input type="text" value={pForm.password} onChange={(e) => setPForm({ ...pForm, password: e.target.value })} data-testid="promote-password" placeholder="min 8 characters" />
            </Field>
            <Field label="Assigned locations">
              <div className="space-y-1.5 rounded-lg border border-slate-200 p-3">
                {locations.map((l) => (
                  <label key={l.id} className="flex cursor-pointer items-center gap-2 text-sm" data-testid={`promote-loc-${l.id}`}>
                    <input type="checkbox" checked={pForm.location_ids.includes(l.id)} onChange={() => toggleLoc(l.id)} className="h-4 w-4 accent-indigo-600" />
                    {l.name}
                  </label>
                ))}
              </div>
            </Field>
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
            {promoteFor?.is_admin
              ? <Button variant="outline" onClick={demote} data-testid="demote-btn" className="text-red-600">Remove admin rights</Button>
              : <span />}
            <Button onClick={savePromote} disabled={!pForm.email || pForm.location_ids.length === 0} data-testid="save-promote" className="bg-[#4338CA] hover:bg-[#3730A3]">
              {promoteFor?.is_admin ? "Update" : "Make location admin"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sv-scroll">
          <DialogHeader><DialogTitle>Add employee</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Full name"><Input value={form.name} onChange={set("name")} data-testid="emp-name" /></Field>
            {user.role === "corporate_admin" && (
              <Field label="Location">
                <Select value={form.location_id} onValueChange={set("location_id")}>
                  <SelectTrigger data-testid="emp-location"><SelectValue placeholder="Select location" /></SelectTrigger>
                  <SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            )}
            <Field label="Job title"><Input value={form.job_title} onChange={set("job_title")} data-testid="emp-title" /></Field>
            <Field label="Photo (optional)">
              <div className="flex items-center gap-4">
                <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded-full border border-slate-200 bg-slate-50">
                  {form.photo_url ? (
                    <img src={form.photo_url} alt="Employee preview" className="h-full w-full object-cover" data-testid="emp-photo-preview" />
                  ) : (
                    <Users className="h-6 w-6 text-slate-300" />
                  )}
                </div>
                <div className="flex-1">
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="emp-photo-upload-label">
                    <Upload className="h-4 w-4" /> Upload photo
                    <input type="file" accept="image/*" className="hidden" onChange={onPhotoFile} data-testid="emp-photo-upload-input" />
                  </label>
                  {form.photo_url && (
                    <button type="button" onClick={() => setForm({ ...form, photo_url: "" })} className="ml-2 text-sm font-semibold text-red-600 hover:underline" data-testid="emp-photo-remove">Remove</button>
                  )}
                  <p className="mt-1 text-xs text-slate-400">JPG or PNG · under 2 MB.</p>
                </div>
              </div>
            </Field>
            <Field label="Hire date (optional)"><Input type="date" value={form.hire_date} onChange={set("hire_date")} /></Field>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
              <div>
                <p className="text-sm font-semibold text-slate-700">Create login account</p>
                <p className="text-xs text-slate-400">Let this employee view their Service CV.</p>
              </div>
              <Switch checked={form.create_account} onCheckedChange={set("create_account")} data-testid="emp-create-account" />
            </div>
            {form.create_account && (
              <>
                <Field label="Account email"><Input type="email" value={form.account_email} onChange={set("account_email")} data-testid="emp-account-email" /></Field>
                <Field label="Temporary password"><Input type="text" value={form.account_password} onChange={set("account_password")} data-testid="emp-account-password" /></Field>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={saving} data-testid="save-employee" className="bg-[#4338CA] hover:bg-[#3730A3]">{saving ? "Saving…" : "Add & generate QR"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return <div><Label className="mb-1 block normal-case tracking-normal text-slate-600">{label}</Label>{children}</div>;
}
